/**
 * JURO scroll racket
 * ------------------------------------------------------------------
 * Drives the scroll-driven 3D racket (sections/juro-scroll-racket.liquid).
 *
 * - Loads the viewer script, and through it three.js and the model, only when
 *   the section is close. On phones a still of the racket stands in until the
 *   3D model has loaded, so the stage is never empty while ~900 KB arrives.
 *   (It briefly replaced the 3D model on phones outright; Julius wants the
 *   racket turning on phones too, 2026-09-18.)
 * - Uses the shared rAF-throttled scroll engine (juro-scroll.js), so this adds
 *   no scroll listener of its own.
 * - Publishes progress as --juro-sr-p and the active caption as data-active,
 *   and passes progress to the viewer's setProgress.
 *
 * Under reduced motion nothing is pinned or animated (see the CSS), the model
 * is still loaded, and it is simply shown face on.
 */

import { subscribe, stageProgress, prefersReducedMotion } from '@theme/juro-scroll';

const loaded = new Map();

function loadScript(src) {
  if (!loaded.has(src)) {
    loaded.set(
      src,
      new Promise((resolve, reject) => {
        const el = document.createElement('script');
        el.src = src;
        el.defer = true;
        el.onload = resolve;
        el.onerror = reject;
        document.head.appendChild(el);
      })
    );
  }
  return loaded.get(src);
}

function mount(section) {
  const stage = section.querySelector('.juro-sr__stage');
  const captions = [...section.querySelectorAll('.juro-sr__caption')];
  const still = section.querySelector('.juro-sr__still');
  const coverLoad = window.innerWidth < 750 && !!still;
  let viewer = null;

  const start = async () => {
    try {
      await loadScript(section.dataset.viewerSrc);
      await customElements.whenDefined('juro-racket-3d');
      viewer = document.createElement('juro-racket-3d');
      viewer.setAttribute('data-scroll-driven', '');
      stage.appendChild(viewer);
      if (coverLoad) {
        // The viewer removes its loader once the model is in; hide the still then.
        const mo = new MutationObserver(() => {
          if (viewer.querySelector('.jr3d-loader')) return;
          mo.disconnect();
          still.hidden = true;
        });
        mo.observe(viewer, { childList: true, subtree: true });
      }
      if (prefersReducedMotion()) viewer.setProgress(0.2);
    } catch (error) {
      section.dataset.srState = 'error';
    }
  };

  if (coverLoad) still.hidden = false;

  const io = new IntersectionObserver(
    (entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      start();
    },
    { rootMargin: '60% 0px' }
  );
  io.observe(section);

  if (prefersReducedMotion() || !captions.length) return;

  let lastActive = -1;
  subscribe((viewportHeight) => {
    const p = stageProgress(section, viewportHeight);
    section.style.setProperty('--juro-sr-p', p.toFixed(4));
    if (viewer) viewer.setProgress(p);

    const active = Math.min(captions.length - 1, Math.floor(p * captions.length));
    if (active !== lastActive) {
      lastActive = active;
      section.dataset.active = String(active);
      captions.forEach((c, i) => c.toggleAttribute('data-active', i === active));
    }
  });
}

function init() {
  for (const section of document.querySelectorAll('.juro-sr:not([data-sr-ready])')) {
    section.setAttribute('data-sr-ready', '');
    mount(section);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
